import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { logException } from 'helpers/loggers';

import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import {
  Button,
  Card,
  Group,
  Indicator,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';

import DockedModal from 'primitives/DockedModal/DockedModal';
import { fetchItemsBySearchTerm } from 'helpers/menuManagement';
import { BADGE_VARIANTS } from 'constants/mantineVariants';
import { TYPE_TO_READABLE_STRING_MAP } from 'constants/menuManagement';
import { breakpoints } from 'styles/theme.variables';

import { CloseIconSm, SearchIconSm } from '@chownow/pantry/icons';
const SEARCH_DEBOUNCE_MS = 300;

export const MenuItemSearchModal = ({
  opened,
  onClose,
  onSubmit,
  companyId,
  merchantId,
  menuUuid,
  initialSearchText = '',
}) => {
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText] = useDebouncedValue(searchText, SEARCH_DEBOUNCE_MS);
  const [options, setOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchErrored, setSearchErrored] = useState(false);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);

  useEffect(() => {
    if (opened) {
      setSearchText(initialSearchText);
      setOptions([]);
      setSelectedValue('');
      setSearchErrored(false);
      setIsLoading(false);
    }
  }, [opened, initialSearchText]);

  useEffect(() => {
    if (!opened) return undefined;

    if (!debouncedSearchText) {
      setOptions([]);
      setSelectedValue('');
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setSearchErrored(false);

    (async () => {
      try {
        const response = await fetchItemsBySearchTerm({
          companyId,
          merchantId,
          menuId: menuUuid,
          searchTerm: debouncedSearchText,
        });
        if (cancelled) return;
        setOptions(
          response.map(item => ({
            label: item?.name,
            value: item?.uuid,
            itemType: item?.item_type,
          })),
        );
      } catch (e) {
        if (!cancelled) setSearchErrored(true);
        logException({ message: 'Failed to fetch menu items', error: e });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedSearchText, opened, companyId, merchantId, menuUuid]);

  const handleDone = () => {
    if (!selectedValue) {
      onClose();
      return;
    }
    const selected = options.find(o => o.value === selectedValue);
    onSubmit({ uuid: selectedValue, label: selected?.label || '' });
    onClose();
  };

  const MAX_LABEL_LENGTH = 20;

  const renderOption = ({ option }) => {
    const match = options.find(opt => opt.value === option.value);
    const label =
      option.label.length > MAX_LABEL_LENGTH
        ? `${option.label.slice(0, MAX_LABEL_LENGTH)}…`
        : option.label;
    return (
      <Group justify="space-between" w="100%" wrap="nowrap">
        <Text variant="sublabel-sm">{label}</Text>
        <Indicator size="8" label={TYPE_TO_READABLE_STRING_MAP[match?.itemType]} variant="active" />
      </Group>
    );
  };

  const dockButtons = <Button onClick={handleDone}>Done</Button>;

  return (
    <DockedModal
      title="Search"
      opened={opened}
      onClose={onClose}
      dockButtons={searchErrored ? null : dockButtons}
      size={isMobile ? 'sm' : 460}
      paddingBottom="md"
      styles={{
        header: {
          padding: isMobile
            ? 'var(--mantine-spacing-md)'
            : 'var(--mantine-spacing-xl) var(--mantine-spacing-xl) var(--mantine-spacing-md)',
        },
      }}
      dockProps={{ noSticky: true, px: isMobile ? 'md' : 'xl', pt: 'xs4', pb: 'xl' }}
    >
      {searchErrored ? (
        <Card p="lg" bg="var(--surface-secondary)" mb="sm">
          <Text variant="sublabel-sm" ta="center">
            Oops! There was an issue loading your data.
          </Text>
        </Card>
      ) : (
        <Stack gap="lg" align="center">
          <TextInput
            label="Enter name or ID"
            placeholder="Search by name or ID"
            value={searchText}
            leftSection={<SearchIconSm />}
            rightSectionPointerEvents="all"
            rightSection={
              isLoading ? (
                <Loader size="xs" />
              ) : (
                <CloseIconSm
                  aria-label="Clear input"
                  onClick={() => setSearchText('')}
                  style={{ display: searchText ? undefined : 'none' }}
                />
              )
            }
            onChange={event => setSearchText(event.currentTarget.value)}
            size="md"
            w="100%"
          />
          <Select
            label="Select an option"
            value={selectedValue || null}
            data={options}
            onChange={value => setSelectedValue(value || '')}
            renderOption={renderOption}
            withCheckIcon={false}
            disabled={options.length === 0}
            rightSection={options.length === 0 ? <span /> : undefined}
            nothingFoundMessage={debouncedSearchText && !isLoading ? 'No matches found' : undefined}
            size="md"
            w="100%"
          />
        </Stack>
      )}
    </DockedModal>
  );
};

MenuItemSearchModal.propTypes = {
  opened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  companyId: PropTypes.number,
  merchantId: PropTypes.number,
  menuUuid: PropTypes.string,
  initialSearchText: PropTypes.string,
};
